import { useState, useCallback } from 'react';

/**
 * Manages the open/closed state and selected client for the ClientSidePanel.
 *
 * Returns helpers that can be passed down to any component that contains a
 * clickable client name (projects-grid, project-details, etc.) without needing
 * to lift state above the nearest common ancestor manually.
 *
 * Usage:
 *   const { activePanelClientId, openClientPanel, closeClientPanel } = useClientPanel();
 */
export function useClientPanel() {
  const [activePanelClientId, setActivePanelClientId] = useState<number | null>(null);

  const openClientPanel = useCallback((clientId: number) => {
    setActivePanelClientId(clientId);
  }, []);

  const closeClientPanel = useCallback(() => {
    setActivePanelClientId(null);
  }, []);

  return {
    /** The ID of the client whose panel is currently open, or null if closed */
    activePanelClientId,
    /** Open the panel for a specific client */
    openClientPanel,
    /** Close the panel */
    closeClientPanel,
    /** Convenience boolean — true when a panel is open */
    isPanelOpen: activePanelClientId !== null,
  };
}
